import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";
import styles from "./CriteriaRow.module.css";
import { translate } from "../../../../../../common/providers";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { CustomButton, CustomTooltip, icon, IconComponent } from "../../../../../../common/components";
import { COMPLIANCE_STATUS, FIELD_STATUS, LINE_HEIGHT } from "../../constants";
import { displayValue, maxCollapseHeight } from "../../utils";

const LINES = 5;
export const CriteriaRow = ({
	criteria,
	detectedValues = null,
	launchInProgress = null,
	onSelectCriteria = null,
	selectedCriteria = null,
}) => {
	const containerRef = useRef(null);
	const [expanded, setExpanded] = useState(false);
	const [showButton, setShowButton] = useState(false);
	const complianceFilters = useSelector(({ projectSheet }) => projectSheet.complianceFilters);
	const values = useMemo(
		() => (isNonEmptyArray(detectedValues?.values) && detectedValues.values) || [],
		[detectedValues]
	);
	const content = useMemo(
		() =>
			isNonEmptyArray(values) &&
			(values?.length === 1
				? displayValue(values[0].value, criteria.type)
				: values.map((val) => displayValue(val.value, criteria.type)).join("<br/>- ")),
		[values, criteria]
	);
	const isVerified = useMemo(() => isNonEmptyArray(values) && values[0]?.status === FIELD_STATUS.VALIDATED, [values]);
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}
		const fullHeight = containerRef.current.scrollHeight;
		setShowButton(fullHeight > maxCollapseHeight(LINES));
	}, [values]);
	const handleSelectCriteria = () => {
		if (onSelectCriteria && typeof onSelectCriteria === "function" && !launchInProgress) {
			const isManuallyAdded = isNonEmptyArray(values) && values[0]?.isManuallyAdded;
			onSelectCriteria({ ...criteria, isManuallyAdded });
		}
	};
	const handleShowMore = () => {
		setExpanded((e) => !e);
	};
	const complianceItems = useMemo(() => {
		const { innerFields } = detectedValues || {};
		const defaultItems = { icon: "", text: "" };
		if (!isNonEmptyArray(innerFields)) {
			return defaultItems;
		}
		const complianceStatusInnerField = innerFields.find((inner) => inner.fieldName === "complianceStatus");
		if (!complianceStatusInnerField) {
			return defaultItems;
		}
		const complianceValue = complianceStatusInnerField?.value?.value?.toUpperCase();
		if (complianceValue === COMPLIANCE_STATUS.COMPLIANT) {
			return {
				icon: "aitCheckCircle",
				text: translate("dashboards.project-sheet.compliance-status.compliant"),
				status: complianceValue,
			};
		}
		if (complianceValue === COMPLIANCE_STATUS.NON_COMPLIANT) {
			return {
				icon: "aitTimesCircle",
				text: translate("dashboards.project-sheet.compliance-status.non-compliant"),
				status: complianceValue,
			};
		}
		if (complianceValue === COMPLIANCE_STATUS.PENDING) {
			return {
				icon: "aitClock",
				text: translate("dashboards.project-sheet.compliance-status.pending"),
				status: complianceValue,
			};
		}
		return defaultItems;
	}, [detectedValues]);
	return (
		<>
			{(!isNonEmptyArray(complianceFilters) ||
				complianceFilters.some((status) => status.toUpperCase() === complianceItems.status)) && (
				<div
					key={criteria.id}
					className={styles.criteria}
					data-is-detection-launched={!!launchInProgress}
					data-is-selected={selectedCriteria?.id === criteria.id}
					role="presentation"
					onClick={handleSelectCriteria}
				>
					{!launchInProgress && (
						<>
							<div className={styles.criteria__header}>
								<CustomTooltip showWhenOverflow title={criteria.name}>
									<div
										className={styles.label}
										data-is-selected={selectedCriteria?.id === criteria.id}
									>
										{criteria.name}
									</div>
								</CustomTooltip>
								{isVerified && (
									<div>
										<CustomTooltip
											title={translate(
												"dashboards.project-sheet.section-list.criteria.is-verified"
											)}
										>
											<IconComponent color="var(--blue-500)" icon={icon.faCheckCircle} />
										</CustomTooltip>
									</div>
								)}
								{complianceItems?.text && (
									<div className={styles.complianceStatus} data-status={complianceItems.status}>
										<IconComponent icon={icon[complianceItems.icon]} size={16} />
										{complianceItems.text}
									</div>
								)}
								<div
									className={styles.criteria__subtext}
									data-has-detected-content={detectedValues?.sourceCount > 0}
								>
									{translate(
										(detectedValues?.sourceCount > 1 &&
											"dashboards.project-sheet.section-list.criteria.detected-content.plural") ||
											"dashboards.project-sheet.section-list.criteria.detected-content.singular",
										{ count: detectedValues?.sourceCount || 0 }
									)}
								</div>
							</div>
							<div className={styles.valueTextContainer}>
								<div
									ref={containerRef}
									className={`multiple-lines-text ${styles.valueText}`}
									/* eslint-disable-next-line react/no-danger */
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(
											content && content !== "N/A"
												? content
												: translate("dashboards.project-sheet.empty-state.value")
										),
									}}
									style={{
										maxHeight: expanded ? "none" : maxCollapseHeight(LINES),
										lineHeight: `${LINE_HEIGHT}px`,
										WebkitLineClamp: expanded ? "none" : LINES,
									}}
								/>
								{showButton && (
									<CustomButton className={styles.seeMore} onClick={handleShowMore}>
										{translate(`common:btn.see-${expanded ? "less" : "more"}`)}
									</CustomButton>
								)}
							</div>
						</>
					)}
				</div>
			)}
		</>
	);
};
